import type { FC, MouseEventHandler, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type PillVariant = 'primary' | 'secondary' | 'inverse' | 'glass'

type PillSize = 'sm' | 'md' | 'lg'

interface PillButtonProps {
    href?: string
    onClick?: MouseEventHandler<HTMLElement>
    variant?: PillVariant
    size?: PillSize
    chevron?: boolean
    type?: 'button' | 'submit'
    disabled?: boolean
    className?: string
    ariaLabel?: string
    children: ReactNode
}

const variantClasses: Record<PillVariant, string> = {
    primary: 'bg-ink text-white hover:bg-[#26262b] focus-visible:outline-violet',
    secondary: 'bg-white text-ink ring-1 ring-inset ring-[#e3e3e9] hover:bg-mist hover:ring-[#d6d6de] focus-visible:outline-violet',
    inverse: 'bg-white text-ink hover:bg-[#ececf0] focus-visible:outline-white',
    glass: 'bg-white/10 text-white ring-1 ring-inset ring-white/20 hover:bg-white/15 focus-visible:outline-white'
}

const sizeClasses: Record<PillSize, string> = {
    sm: 'h-9 gap-1.5 px-4 text-[13.5px]',
    md: 'h-11 gap-2 px-5 text-[15px]',
    lg: 'h-12 gap-2 px-6 text-base'
}

export const PillButton: FC<PillButtonProps> = ({
    href,
    onClick,
    variant = 'primary',
    size = 'md',
    chevron = false,
    type = 'button',
    disabled = false,
    className,
    ariaLabel,
    children
}): ReactNode => {
    const classes = cn(
        'group inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-full font-medium tracking-[-0.01em] transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-[3px] disabled:pointer-events-none disabled:opacity-50',
        variantClasses[variant],
        sizeClasses[size],
        className
    )

    const content = (
        <>
            <span>
                {children}
            </span>

            {chevron && (
                <ChevronRight
                    aria-hidden='true'
                    className='-mr-1 size-4 transition-transform duration-300 ease-out group-hover:translate-x-[3px]'
                />
            )}
        </>
    )

    if (href) {
        return (
            <Link href={href} onClick={onClick} className={classes} aria-label={ariaLabel}>
                {content}
            </Link>
        )
    }

    return (
        <button type={type} onClick={onClick} disabled={disabled} className={classes} aria-label={ariaLabel}>
            {content}
        </button>
    )
}
